import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, CheckCircle2, AlertTriangle, XCircle, Info } from 'lucide-react';
import styles from './Toast.module.css';

const ToastContext = createContext(null);

// Leading icon per variant — a status glyph makes the outcome legible at a
// glance (a green check reads "done" before the copy is even parsed).
const VARIANT_ICON = {
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
  info: Info,
};

// Framer owns the container enter/exit; these mirror the CSS token *values*
// (--dur-base 0.22s, --ease-out cubic-bezier(0.16,1,0.3,1)) so toasts move on
// the same curve as the rest of the app even though framer can't read CSS vars.
const ENTER = { duration: 0.22, ease: [0.16, 1, 0.3, 1] };

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    ({ message, title, variant = 'info', duration = 4000 }) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, message, title, variant }]);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  return (
    <ToastContext.Provider value={{ show, dismiss }}>
      {children}
      <div className={styles.stack}>
        <AnimatePresence>
          {toasts.map((t) => {
            const Icon = VARIANT_ICON[t.variant] || Info;
            return (
              <motion.div
                key={t.id}
                className={styles.toast}
                data-variant={t.variant}
                initial={{ opacity: 0, y: -8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, x: 40 }}
                transition={ENTER}
                role="status"
                aria-live="polite"
              >
                <span className={styles.icon} aria-hidden="true">
                  <Icon size={18} strokeWidth={2.25} />
                </span>
                <div className={styles.body}>
                  {t.title && <div className={styles.title}>{t.title}</div>}
                  <div className={styles.message}>{t.message}</div>
                </div>
                <button className={styles.closeBtn} onClick={() => dismiss(t.id)} aria-label="Dismiss">
                  <X size={14} />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
